import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * Scroll behaviour for client-side navigation.
 *
 * A plain route change goes to the top, as before. A link carrying a hash has
 * to reach that section instead: the dataset pages link their headline stats to
 * the rows behind them (`?remote=1#rows`), and without this the reader lands at
 * the top of the page wondering what the link did.
 *
 * Three things make the hash case awkward, all measured on /data/credentials:
 *  - the target is rendered from data that arrives after the effect first runs,
 *    so a single querySelector on mount finds nothing;
 *  - following a stat link ALSO changes the filter, so the page refetches and
 *    everything above the target changes height mid-scroll, leaving a one-shot
 *    scroll nowhere near where it aimed;
 *  - requestAnimationFrame is the obvious way to retry and the wrong one: it is
 *    paused while the tab is in the background, so the retry silently never
 *    runs. setTimeout is throttled there but still fires.
 *
 * So: retry on a timer until the target is at the top or a short deadline
 * passes, and scroll instantly rather than smoothly, since the content under
 * the reader is being replaced regardless.
 *
 * A same-path change with no hash (toggling a filter chip) deliberately does
 * NOT scroll; yanking the page to the top on every chip press is worse than
 * staying put.
 */
const SETTLE_MS = 2000;
const STEP_MS = 60;
const TOLERANCE_PX = 8;

export const ScrollToTop = () => {
  const { pathname, hash } = useLocation();

  useEffect(() => {
    if (!hash) {
      window.scrollTo(0, 0);
      return;
    }
    const id = decodeURIComponent(hash.slice(1));
    let timer = 0;
    const deadline = Date.now() + SETTLE_MS;
    const tick = () => {
      const el = document.getElementById(id);
      if (el) {
        if (Math.abs(el.getBoundingClientRect().top) <= TOLERANCE_PX) return; // settled
        el.scrollIntoView({ behavior: 'auto', block: 'start' });
      }
      if (Date.now() < deadline) timer = window.setTimeout(tick, STEP_MS);
    };
    tick();
    return () => window.clearTimeout(timer);
  }, [pathname, hash]);

  return null;
};
